import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, Alert, Snackbar, Tooltip,
  Accordion, AccordionSummary, AccordionDetails, FormControlLabel, Checkbox, Divider, Grid, Badge
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon, AdminPanelSettings as AdminIcon, Group as GroupIcon, Check as CheckIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import RoleService from '../../services/role.service';
import PermissionService from '../../services/permission.service';

const EnhancedRolesManagement = () => {
  const { hasPermission } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rolesRes, permissionsRes] = await Promise.all([
        RoleService.getAll(),
        PermissionService.getAll()
      ]);
      
      console.log('Roles response:', rolesRes.data);
      console.log('Permissions response:', permissionsRes.data);
      
      setRoles(rolesRes.data || []);
      setPermissions(permissionsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Error loading data', 'error');
      setRoles([]);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name,
        description: role.description || '',
        permissions: role.permissions?.map(p => p.id) || []
      });
    } else {
      setEditingRole(null);
      setFormData({
        name: '',
        description: '',
        permissions: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
      permissions: []
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await RoleService.update(editingRole.id, formData);
        showSnackbar('Role updated successfully');
      } else {
        await RoleService.create(formData);
        showSnackbar('Role created successfully');
      }

      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error saving role:', error);
      const msg = error.response?.data?.message
        || (error.response?.data?.errors && typeof error.response.data.errors === 'object'
          ? Object.values(error.response.data.errors).flat().join(', ')
          : null)
        || error.response?.data?.error
        || error.message
        || 'Error saving role';
      showSnackbar(msg, 'error');
    }
  };

  const handleDelete = async (roleId) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      try {
        await RoleService.delete(roleId);
        showSnackbar('Role deleted successfully');
        loadData();
      } catch (error) {
        console.error('Error deleting role:', error);
        showSnackbar('Error deleting role', 'error');
      }
    }
  };

  const handlePermissionChange = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(id => id !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const groupedPermissions = (Array.isArray(permissions) ? permissions : []).reduce((acc, permission) => {
    if (permission && permission.name) {
      const [module] = permission.name.split('.');
      if (!acc[module]) {
        acc[module] = [];
      }
      acc[module].push(permission);
    }
    return acc;
  }, {});

  const getPermissionCount = (role) => {
    return role.permissions?.length || 0;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading roles...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" component="h1">
              Role Management
            </Typography>
            {hasPermission('roles.create') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Add Role
              </Button>
            )}
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Role Name</TableCell>
                  <TableCell>Permissions</TableCell>
                  <TableCell>Permission Count</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AdminIcon color="primary" />
                        <Typography variant="body1" fontWeight="medium">
                          {role.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap" maxWidth="300px">
                        {role.permissions?.slice(0, 3).map((permission) => (
                          <Chip
                            key={permission.id}
                            label={permission.name}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        ))}
                        {role.permissions?.length > 3 && (
                          <Chip
                            label={`+${role.permissions.length - 3} more`}
                            size="small"
                            color="default"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Badge badgeContent={getPermissionCount(role)} color="primary">
                        <SecurityIcon color="action" />
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(role.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1}>
                        {hasPermission('roles.edit') && (
                          <Tooltip title="Edit role">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(role)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('roles.delete') && (
                          <Tooltip title="Delete role">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(role.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Role Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <GroupIcon color="primary" />
              {editingRole ? 'Edit Role' : 'Create Role'}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <TextField
                label="Role Name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  name: e.target.value
                }))}
                required
                fullWidth
                placeholder="e.g., Manager, Editor, Viewer"
              />

              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                minRows={2}
                placeholder="Optional description to help other admins understand this role"
              />

              <Divider />

              <Typography variant="h6" gutterBottom>
                Permissions
              </Typography>

              <Box maxHeight="400px" overflow="auto">
                {Object.entries(groupedPermissions || {}).map(([module, modulePermissions]) => (
                  <Accordion key={module} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {module.charAt(0).toUpperCase() + module.slice(1)} ({modulePermissions.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={1}>
                        {modulePermissions.map((permission) => (
                          <Grid item xs={12} sm={6} md={4} key={permission.id}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.permissions.includes(permission.id)}
                                  onChange={() => handlePermissionChange(permission.id)}
                                  color="primary"
                                />
                              }
                              label={
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <Typography variant="body2">
                                    {permission.name}
                                  </Typography>
                                  {formData.permissions.includes(permission.id) && (
                                    <CheckIcon fontSize="small" color="primary" />
                                  )}
                                </Box>
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>

              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  Selected: {formData.permissions.length} permissions
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingRole ? 'Update Role' : 'Create Role'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EnhancedRolesManagement;
